import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const AREA_META = {
  health:        { label: 'Health',          color: '#4caf7d', icon: '💪' },
  mind:          { label: 'Mind',            color: '#9b5ce0', icon: '🧠' },
  career:        { label: 'Career & Growth', color: '#c9a84c', icon: '📈' },
  relationships: { label: 'Relationships',   color: '#e05c5c', icon: '❤️' },
  faith:         { label: 'Faith',           color: '#5c8fe0', icon: '✝️' },
  purpose:       { label: 'Goals',         color: '#e07d3c', icon: '🎯' },
}

function getLocalDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

export default function LifeAreaPage({ area, onNavigate }) {
  const { user } = useAuth()
  const [history, setHistory] = useState([])
  const [todayScore, setTodayScore] = useState(0)
  const [loading, setLoading] = useState(true)
  const meta = AREA_META[area] || AREA_META.health
  const scoreKey = `${area}_score`

  useEffect(() => {
    if (user) loadHistory()
  }, [user, area])

  async function loadHistory() {
    setLoading(true)

    // Load all tasks for this area
    const { data } = await supabase
      .from('manual_tasks')
      .select('date, area, final_points')
      .eq('user_id', user.id)
      .eq('area', area)
      .eq('flagged', false)
      .order('date', { ascending: true })

    if (data && data.length > 0) {
      // Group by date
      const byDate = {}
      data.forEach(t => {
        byDate[t.date] = (byDate[t.date] || 0) + t.final_points
      })
      const rows = Object.entries(byDate).map(([date, pts]) => ({
        date,
        [scoreKey]: Math.min(100, pts)
      }))
      setHistory(rows)

      // Today's score
      const today = getLocalDate()
      setTodayScore(byDate[today] || 0)
    } else {
      setHistory([])
      setTodayScore(0)
    }

    setLoading(false)
  }

  const scores = history.map(h => h[scoreKey] || 0).filter(s => s > 0)
  const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0
  const bestScore = scores.length ? Math.max(...scores) : 0
  const bestDay = history.find(h => h[scoreKey] === bestScore)

  function formatDate(dateStr) {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // SVG line chart
  const chartW = 320
  const chartH = 100
  const pad = 8
  const maxVal = Math.max(...scores, 100)

  function getX(i) {
    if (scores.length <= 1) return chartW / 2
    return pad + (i / (scores.length - 1)) * (chartW - pad * 2)
  }
  function getY(val) {
    return chartH - pad - ((val / maxVal) * (chartH - pad * 2))
  }

  const linePath = scores.length > 1
    ? scores.map((v, i) => `${i === 0 ? 'M' : 'L'}${getX(i).toFixed(1)},${getY(v).toFixed(1)}`).join(' ')
    : ''

  const areaPath = scores.length > 1
    ? `${linePath} L${getX(scores.length - 1).toFixed(1)},${chartH} L${getX(0).toFixed(1)},${chartH} Z`
    : ''

  return (
    <div style={s.page}>
      <div style={s.ambient} />

      <div style={s.header}>
        <div style={s.backBtn} onClick={() => onNavigate('dashboard')}>←</div>
        <div style={{ ...s.headerTitle, color: meta.color }}>{meta.label}</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.main}>

        {loading ? (
          <div style={s.loading}>Loading...</div>
        ) : (
          <>
            {/* SCORE CARDS */}
            <div style={s.statsRow}>
              <div style={s.statCard}>
                <div style={s.statLabel}>Today</div>
                <div style={{ ...s.statVal, color: meta.color }}>{todayScore}</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>Average</div>
                <div style={{ ...s.statVal, color: meta.color }}>{avgScore}</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>Best Day</div>
                <div style={{ ...s.statVal, color: meta.color }}>{bestScore}</div>
                <div style={s.statDate}>{formatDate(bestDay?.date)}</div>
              </div>
            </div>

            {/* LINE CHART */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Over Time</div>
              <div style={s.chartCard}>
                {scores.length < 2 ? (
                  <div style={s.empty}>Check in more days to see your trend.</div>
                ) : (
                  <div style={s.chartWrap}>
                    {[25, 50, 75, 100].map(v => (
                      <div key={v} style={{ ...s.gridLine, bottom: `${v}%` }}>
                        <span style={s.gridLabel}>{v}</span>
                      </div>
                    ))}
                    <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', height: '130px', overflow: 'visible' }} preserveAspectRatio="none">
                      <defs>
                        <linearGradient id={`grad-${area}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={meta.color} stopOpacity="0.3"/>
                          <stop offset="100%" stopColor={meta.color} stopOpacity="0.02"/>
                        </linearGradient>
                      </defs>
                      <path d={areaPath} fill={`url(#grad-${area})`}/>
                      <path d={linePath} fill="none" stroke={meta.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      {scores.length <= 14 && scores.map((v, i) => (
                        <circle key={i} cx={getX(i)} cy={getY(v)} r="3" fill={meta.color} opacity="0.8"/>
                      ))}
                      <circle cx={getX(scores.length - 1)} cy={getY(scores[scores.length - 1])} r="4.5" fill={meta.color} style={{ filter: `drop-shadow(0 0 4px ${meta.color})` }}/>
                    </svg>
                    <div style={s.xAxis}>
                      {history.filter((_, i) => i === 0 || i === Math.floor(history.length / 2) || i === history.length - 1).map(entry => (
                        <div key={entry.date} style={s.xLabel}>{formatDate(entry.date)}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* DAYS LOGGED */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Activity</div>
              <div style={s.activityCard}>
                <div style={s.activityRow}>
                  <div style={s.activityLabel}>Days logged</div>
                  <div style={{ ...s.activityVal, color: meta.color }}>{history.length}</div>
                </div>
                <div style={{ ...s.activityRow, borderBottom: 'none' }}>
                  <div style={s.activityLabel}>Completion rate</div>
                  <div style={{ ...s.activityVal, color: meta.color }}>
                    {history.length ? Math.round((scores.length / history.length) * 100) : 0}%
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(ellipse, rgba(201,168,76,0.22) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(6,6,8,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '24px 20px 100px', position: 'relative', zIndex: 1 },
  loading: { textAlign: 'center', color: '#6b6860', padding: '40px 0' },
  statsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '28px' },
  statCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '16px 12px', textAlign: 'center' },
  statLabel: { fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '6px' },
  statVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', lineHeight: 1 },
  statDate: { fontSize: '10px', color: '#6b6860', marginTop: '4px' },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '12px' },
  chartCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '20px' },
  empty: { textAlign: 'center', color: '#6b6860', fontSize: '13px', padding: '20px 0' },
  chartWrap: { position: 'relative', paddingLeft: '28px' },
  gridLine: { position: 'absolute', left: '28px', right: 0, height: '1px', background: 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center' },
  gridLabel: { position: 'absolute', left: '-28px', fontSize: '9px', color: 'rgba(255,255,255,0.2)', width: '22px', textAlign: 'right' },
  xAxis: { display: 'flex', justifyContent: 'space-between', marginTop: '6px' },
  xLabel: { fontSize: '9px', color: '#6b6860' },
  activityCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '4px 0' },
  activityRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  activityLabel: { fontSize: '13px', color: '#e8e6df' },
  activityVal: { fontSize: '15px', fontWeight: '600' },
}
